import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';

export interface QueryResultResponse {
    status: string;
    taskId?: string;
    queryId?: number;
    count?: number;
    personIds?: number[];
    listCount?: Array<{ channel: string; count: number }>;
    message?: string;
    description?: string;
}

const UNAVAILABLE: QueryResultResponse = { status: 'UNKNOWN OR EXPIRED' };
interface FetchQueryResultOptions {
    keepPreviousResult?: boolean;
}

export const useQueryResult = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<QueryResultResponse | null>(null);

    const fetchQueryResult = useCallback(async (
        queryId: string | number,
        options: FetchQueryResultOptions = {}
    ): Promise<QueryResultResponse | null> => {
        if (!isReady || !queryId) return null;

        setLoading(true);
        setError(null);
        if (!options.keepPreviousResult) setResult(null);

        try {
            const response = await authFetch(`/getQueryResult?queryId=${queryId}`, { method: 'GET' });
            const typed = (response ?? UNAVAILABLE) as QueryResultResponse;
            setResult(typed);
            return typed;
        } catch (err) {
            const msg = err instanceof Error ? err.message : '';
            if (msg.toLowerCase().includes('http 400') || msg.toLowerCase().includes('bad request')) {
                setResult(UNAVAILABLE);
                return UNAVAILABLE;
            }
            setError(msg || 'Erreur lors de la récupération du résultat');
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady]);

    const markAsRunning = useCallback((taskId?: string) => {
        setError(null);
        setResult({ status: 'IN_PROGRESS', ...(taskId && { taskId }) });
    }, []);

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    return { fetchQueryResult, loading, error, result, description: result?.description, reset, markAsRunning };
};
